import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { PageIntro } from "@/components/PageIntro";
import { ProjectCard, spanFor } from "@/components/ProjectCard";
import { ProjectModal } from "@/components/ProjectModal";
import { CtaBanner } from "@/components/CtaBanner";
import { CATEGORIES, PROJECTS } from "@/data/projects";

const slugify = (c) => c.toLowerCase().replace(/\s+/g, "-");

export default function Portfolio() {
  const [filter, setFilter] = useState(CATEGORIES[0]);
  const [params, setParams] = useSearchParams();
  const items = useMemo(() => (filter === CATEGORIES[0] ? PROJECTS : PROJECTS.filter((p) => p.categories.includes(filter))), [filter]);

  const openSlug = params.get("project");
  const idx = PROJECTS.findIndex((p) => p.slug === openSlug);
  const project = idx >= 0 ? PROJECTS[idx] : null;
  const next = project ? PROJECTS[(idx + 1) % PROJECTS.length] : null;

  return (
    <>
      <Seo
        title="Portfolio — Concept Branding, Packaging & Digital Design Projects"
        description="Explore PRPO LTD concept projects across brand identity, logo design, packaging, brochures, marketing campaigns, website and mobile interface design."
        path="/portfolio"
      />
      <PageIntro
        testid="portfolio-intro"
        label="Portfolio"
        lines={[{ content: "Selected concepts," }, { content: "crafted with intent.", className: "text-royal" }]}
        text="Every project below is a clearly labelled concept or speculative design created to demonstrate PRPO LTD’s approach to branding, packaging and digital design. None is presented as client work."
      />
      <section className="pb-24 sm:pb-36" aria-label="Concept projects">
        <div className="shell">
          <div role="group" aria-label="Filter projects by category" className="flex flex-wrap gap-2 border-y border-[var(--line)] py-5" data-testid="portfolio-filters">
            {CATEGORIES.map((c) => {
              const active = c === filter;
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(c)}
                  data-testid={`portfolio-filter-${slugify(c)}`}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${active ? "bg-navy text-white" : "text-navy hover:bg-mist"}`}
                >
                  {c}
                  {active && <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-lime align-middle" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
          <p className="mt-5 text-sm text-slatey" aria-live="polite" data-testid="portfolio-count">
            Showing {items.length} concept {items.length === 1 ? "project" : "projects"}
          </p>
          <motion.div layout className="mt-10 grid gap-x-6 gap-y-14 md:grid-cols-12" data-testid="portfolio-grid">
            <AnimatePresence mode="popLayout">
              {items.map((p, i) => (
                <motion.div
                  key={p.slug}
                  layout
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className={spanFor(i)}
                >
                  <ProjectCard project={p} index={i} wide={spanFor(i).includes("7")} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
      <CtaBanner lines={["Got a brief of", "your own? Let’s talk."]} />
      <ProjectModal
        project={project}
        next={next}
        onClose={() => setParams({}, { preventScrollReset: true })}
        onNext={() => setParams({ project: next.slug }, { preventScrollReset: true })}
      />
    </>
  );
}
